"use client"

// src/components/pages/MenuManagementPage.jsx
import { useState, useEffect, useCallback } from "react"
import DashboardTemplate from "../templates/DashboardTemplate"
import Input from "../atoms/Input"
import Button from "../atoms/Button"
import Card from "../molecules/Card"
import api from "../../services/api"
import { useAuth } from "../../hooks/useAuth"
import FileUpload from "../atoms/FileUpload"
import Toast from "../atoms/Toast"

const BACKEND_URL = process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '')


const Spinner = () => (
  <div className="flex justify-center items-center py-12">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
  </div>
)

const ItemEditModal = ({ item, onClose, onConfirm }) => {
  const [editedItem, setEditedItem] = useState({
    name: item.name,
    price: item.price,
    description: item.description,
    imageUrl: item.imageUrl || "",
  })

  const handleChange = (e) => {
    setEditedItem({ ...editedItem, [e.target.name]: e.target.value })
  }

  const handleFileUpload = (url) => {
    setEditedItem({ ...editedItem, imageUrl: url })
  }

  const resolvedImage = (url) =>
    url.match(/^https?:\/\//)
      ? url
      : `${BACKEND_URL}${url.startsWith("/") ? "" : "/"}${url}`

 return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center mb-4 pb-2 border-b">
          <h2 className="text-xl font-bold text-gray-800">Edit Item: {item.name}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-amber-500 rounded-full p-1 transition-colors duration-200"
            aria-label="Close Modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Item Name
            </label>
            <Input id="name" name="name" value={editedItem.name} onChange={handleChange} placeholder="Item Name" />
          </div>

          <div>
            <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
              Price
            </label>
            <Input
              id="price"
              name="price"
              type="number"
              step="0.01"
              value={editedItem.price}
              onChange={handleChange}
              placeholder="Price"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <Input
              id="description"
              name="description"
              value={editedItem.description}
              onChange={handleChange}
              placeholder="Description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Item Image</label>
            <FileUpload onUpload={handleFileUpload} />
          </div>

          {editedItem.imageUrl ? (
            <img
              src={resolvedImage(editedItem.imageUrl)}
              alt={editedItem.name}
              className="rounded mb-2 max-h-48 object-cover w-full"
            />
          ) : (
            <div className="h-32 bg-gray-200 flex items-center justify-center text-gray-500 rounded mb-2">
              No Image
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button onClick={onClose} className="bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-500">
            Cancel
          </Button>
          <Button onClick={() => onConfirm(editedItem)}>
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  )
}

const MenuManagementPage = () => {
  const { user } = useAuth()
  const ownerId = user ? user._id || user.id : null

  const [menu, setMenu] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [restaurantId, setRestaurantId] = useState(null)
  const [newCategoryHeader, setNewCategoryHeader] = useState("")
  const [newItems, setNewItems] = useState({})
  const [notification, setNotification] = useState(null)
  const [editItemData, setEditItemData] = useState(null)
  const [editCategoryIndex, setEditCategoryIndex] = useState(null)

  const showNotification = (type, message) => {
    setNotification({ type, message })
    setTimeout(() => setNotification(null), 5000)
  }

  // Transform menu data for update (store only IDs)
  const transformMenuForUpdate = (menuData, restId) => {
    const transformedCategories = menuData.categories.map((category) => ({
      header: category.header,
      items: category.items.map((item) => {
        if (typeof item === "object" && item._id) {
          return String(item._id)
        }
        return String(item)
      }),
    }))
    return {
      restaurant: String(restId),
      title: menuData.title,
      categories: transformedCategories,
    }
  }

  // Load menu from backend with populated items
  const loadMenu = useCallback(async () => {
    try {
      if (!ownerId) throw new Error("Owner ID is missing")
      const resRestaurant = await api.get(`/restaurants/owner/${ownerId}`)
      if (!resRestaurant.data) throw new Error("No restaurant found for this owner")
      const restId = resRestaurant.data._id
      setRestaurantId(restId)
      const resMenu = await api.get(`/menus/restaurant/${restId}`)
      if (resMenu.data && resMenu.data.length > 0) {
        setMenu(resMenu.data[0])
      } else {
        setMenu({
          restaurant: String(restId),
          title: "Main Menu",
          categories: [],
        })
      }
      setError(null)
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message
      setError(errMsg)
    } finally {
      setLoading(false)
    }
  }, [ownerId])

  useEffect(() => {
    if (user && ownerId) {
      loadMenu()
    }
  }, [user, ownerId, loadMenu])

  // Handler to add a new category
  const handleAddCategory = () => {
    if (newCategoryHeader.trim() === "") {
      showNotification("error", "Category header cannot be empty")
      return
    }
    const updatedCategories = menu.categories ? [...menu.categories] : []
    updatedCategories.push({ header: newCategoryHeader, items: [] })
    setMenu({ ...menu, categories: updatedCategories })
    setNewCategoryHeader("")
    showNotification("success", "Category added successfully")
  }

  // Handler to update new item details for a category
  const handleNewItemChange = (catIndex, field, value) => {
    setNewItems({
      ...newItems,
      [catIndex]: {
        ...newItems[catIndex],
        [field]: value,
      },
    })
  }

  // Handler to add a new item to a category
  const handleAddItem = async (catIndex) => {
    const itemData = newItems[catIndex]
    if (!itemData || !itemData.name?.trim() || !itemData.price) {
      showNotification("error", "Please provide at least the item name and price.")
      return
    }
    try {
      const resItem = await api.post("/items", {
        name: itemData.name,
        description: itemData.description || "",
        price: Number.parseFloat(itemData.price),
        imageUrl: itemData.imageUrl || "",
      })
      const newItemId = String(resItem.data._id)
      const updatedCategories = [...menu.categories]
      if (!updatedCategories[catIndex].items) {
        updatedCategories[catIndex].items = []
      }
      updatedCategories[catIndex].items.push(newItemId)
      const payload = transformMenuForUpdate({ ...menu, categories: updatedCategories }, restaurantId)
      let updatedMenu
      if (menu._id) {
        updatedMenu = await api.put(`/menus/${menu._id}`, payload)
      } else {
        updatedMenu = await api.post("/menus", payload)
      }
      setMenu(updatedMenu.data)
      setNewItems({ ...newItems, [catIndex]: { name: "", price: "", description: "", imageUrl: "" } })
      showNotification("success", "Item added successfully")
    } catch (err) {
      console.error("Failed to add menu item:", err)
      const errMsg = err.response?.data?.error || err.message
      showNotification("error", "Failed to add menu item: " + errMsg)
    }
  }

  // Handler to delete an item from a category
  const handleDeleteItem = async (catIndex, itemId) => {
    try {
      await api.delete(`/items/${itemId}`)
      const updatedCategories = [...menu.categories]
      updatedCategories[catIndex].items = updatedCategories[catIndex].items.filter(
        (item) => String(item._id || item) !== String(itemId),
      )
      const payload = transformMenuForUpdate({ ...menu, categories: updatedCategories }, restaurantId)
      let updatedMenu
      if (menu._id) {
        updatedMenu = await api.put(`/menus/${menu._id}`, payload)
      } else {
        updatedMenu = await api.post("/menus", payload)
      }
      setMenu(updatedMenu.data)
      showNotification("success", "Item deleted successfully")
    } catch (err) {
      console.error("Failed to delete item:", err)
      const errMsg = err.response?.data?.error || err.message
      showNotification("error", "Failed to delete item: " + errMsg)
    }
  }

  // Handler to open edit modal for an item
  const handleEditItem = (catIndex, item) => {
    setEditCategoryIndex(catIndex)
    setEditItemData(item)
  }

  // Handler for confirming item edit
  const handleConfirmEditItem = async (editedData) => {
    try {
      await api.put(`/items/${editItemData._id}`, editedData)
      await loadMenu()
      setEditItemData(null)
      setEditCategoryIndex(null)
      showNotification("success", "Item updated successfully")
    } catch (err) {
      console.error("Failed to update item:", err)
      const errMsg = err.response?.data?.error || err.message
      showNotification("error", "Failed to update item: " + errMsg)
    }
  }

  // Handler to close the edit modal
  const handleCloseEditModal = () => {
    setEditItemData(null)
    setEditCategoryIndex(null)
  }

  const handleSaveMenu = async () => {
    try {
      const payload = transformMenuForUpdate(menu, restaurantId)
      let updatedMenu
      if (menu._id) {
        updatedMenu = await api.put(`/menus/${menu._id}`, payload)
      } else {
        updatedMenu = await api.post("/menus", payload)
      }
      setMenu(updatedMenu.data)
      showNotification("success", "Menu saved successfully")
    } catch (err) {
      console.error("Failed to save menu:", err)
      const errMsg = err.response?.data?.error || err.message
      showNotification("error", "Failed to save menu: " + errMsg)
    }
  }

const resolveImage = (url) =>
    url.match(/^https?:\/\//)
      ? url
      : `${BACKEND_URL}${url.startsWith("/") ? "" : "/"}${url}`

  if (loading) {
    return (
      <DashboardTemplate>
        <Spinner />
      </DashboardTemplate>
    )
  }
  if (error) {
    return (
      <DashboardTemplate>
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </DashboardTemplate>
    )
  }

  return (
    <DashboardTemplate>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Menu Management</h1>
            <p className="text-gray-600">Create and manage your restaurant's menu</p>
          </div>
          <Button onClick={handleSaveMenu} className="mt-4 md:mt-0">
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
              ></path>
            </svg>
            Save Menu
          </Button>
        </div>

        {notification && (
          <Toast
            message={notification.message}
            type={notification.type === "error" ? "error" : "success"}
            onClose={() => setNotification(null)}
          />
        )}

        {/* Section to add a new category */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold mb-4 text-gray-800">Add New Category</h2>
          <div className="flex flex-col sm:flex-row items-stretch gap-3">
            <div className="flex-grow">
              <Input
                placeholder="Category Name (e.g., Appetizers, Main Courses, Desserts)"
                value={newCategoryHeader}
                onChange={(e) => setNewCategoryHeader(e.target.value)}
              />
            </div>
            <Button onClick={handleAddCategory} className="sm:ml-2 whitespace-nowrap">
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                ></path>
              </svg>
              Add Category
            </Button>
          </div>
        </div>

        {/* List existing categories and allow adding/editing/deleting items */}
        {menu.categories && menu.categories.length > 0 ? (
          menu.categories.map((category, catIndex) => (
            <div key={catIndex} className="mb-10 bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <Input
                  value={category.header}
                  onChange={(e) => {
                    const updatedCategories = [...menu.categories]
                    updatedCategories[catIndex].header = e.target.value
                    setMenu({ ...menu, categories: updatedCategories })
                  }}
                  placeholder="Category Header"
                  className="text-xl font-bold"
                />
              </div>

              <div className="p-6 bg-gray-50 border-b border-gray-100">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">Add New Item to {category.header}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Item Name</label>
                    <Input
                      placeholder="Item Name"
                      value={newItems[catIndex]?.name || ""}
                      onChange={(e) => handleNewItemChange(catIndex, "name", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                    <Input
                      placeholder="Price"
                      type="number"
                      step="0.01"
                      value={newItems[catIndex]?.price || ""}
                      onChange={(e) => handleNewItemChange(catIndex, "price", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <Input
                      placeholder="Description"
                      value={newItems[catIndex]?.description || ""}
                      onChange={(e) => handleNewItemChange(catIndex, "description", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                    <FileUpload onUpload={(url) => handleNewItemChange(catIndex, "imageUrl", url)} />
                  </div>
                </div>
                <div className="mt-4">
                  <Button onClick={() => handleAddItem(catIndex)}>
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      ></path>
                    </svg>
                    Add Item
                  </Button>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-800">Items in {category.header}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {category.items && category.items.length > 0 ? (
                    category.items.map((item, index) => (
                      <Card
                        key={index}
                        header={<h3 className="font-semibold text-lg text-gray-800">{item.name || "Item"}</h3>}
                        footer={
                          <div className="flex justify-between items-center">
                            <span className="text-lg font-bold text-amber-600">${item.price?.toFixed(2) || "N/A"}</span>
                            <div className="flex space-x-2">
                              <Button
                                onClick={() => handleEditItem(catIndex, item)}
                                className="bg-amber-100 text-amber-800 hover:bg-amber-200"
                              >
                                <svg
                                  className="w-5 h-5 mr-1"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  ></path>
                                </svg>
                                Edit
                              </Button>
                              <Button
                                onClick={() => handleDeleteItem(catIndex, item._id || item)}
                                className="bg-red-100 text-red-800 hover:bg-red-200"
                              >
                                <svg
                                  className="w-5 h-5 mr-1"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  ></path>
                                </svg>
                                Delete
                              </Button>
                            </div>
                          </div>
                        }
                        className="hover:shadow-md transition-shadow duration-300"
                      >
                        <p className="text-gray-600 mb-3">{item.description || ""}</p>
                            {item.imageUrl ? (
                      <img
                        src={resolveImage(item.imageUrl)}
                        alt={item.name}
                        className="w-full h-48 object-cover rounded-md"
                      />
                    ) : (
                      <div className="h-32 w-full flex items-center justify-center bg-gray-100 text-gray-500 rounded-md">
                        No Image
                      </div>
                    )}
                  </Card>
                    ))
                  ) : (
                    <div className="col-span-2 bg-gray-50 rounded-lg p-8 text-center">
                      <svg
                        className="w-12 h-12 text-gray-400 mx-auto mb-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        ></path>
                      </svg>
                      <p className="text-gray-500">No items in this category</p>
                      <p className="text-gray-400 text-sm mt-1">Add items using the form above</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-gray-50 rounded-lg p-8 text-center">
            <svg
              className="w-16 h-16 text-gray-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              ></path>
            </svg>
            <p className="text-gray-500 text-lg">No categories found</p>
            <p className="text-gray-400 mt-2">Add a category to start building your menu</p>
          </div>
        )}

        {/* Global Save Menu Button */}
        <div className="mt-8">
          <Button onClick={handleSaveMenu} className="w-full py-3 text-lg">
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
              ></path>
            </svg>
            Save Menu
          </Button>
        </div>
      </div>

      {editItemData && (
        <ItemEditModal item={editItemData} onClose={handleCloseEditModal} onConfirm={handleConfirmEditItem} />
      )}
    </DashboardTemplate>
  )
}

export default MenuManagementPage
